import { ListChecks, Settings2, UserCircle } from 'lucide-react';
import { InfoPanel } from '@/components/shared/InfoPanel';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { AgentConfigDetailFields } from '@reflex/ui/components/agent/AgentConfigDetailFields';
import { DetailField, DetailList } from '@reflex/ui/components/shared/DetailList';
import { ResourceChip } from '@reflex/ui/components/resource-preview/ResourceChip';
import { useAgentPersona } from '@reflex/plugin-agent-persona/web/useAgentPersonas';
import type { Agent } from '@/lib/types';
import { getGitRepoAttachment } from '@reflex/shared';
import { AgentSubscriptionSelector } from './AgentSubscriptionSelector';

export function AgentDetailParamsSection({ agent }: { agent: Agent }) {
  const gitRepo = getGitRepoAttachment(agent);
  // Only the persona id is persisted on the run; resolve the current name
  // live (hook no-ops on the empty id). A persona that no longer resolves
  // leaves the chip on its raw-id fallback.
  const { data: persona } = useAgentPersona(agent.personaId ?? '');
  const envVarCount = agent.envVars?.length ?? 0;
  const hasAnyDetail =
    !!agent.model ||
    !!gitRepo?.repoSlug ||
    !!gitRepo?.repoBranch ||
    !!agent.blueprintId ||
    !!agent.modelProviderSecretId ||
    !!agent.personaId ||
    envVarCount > 0;

  return (
    <section data-testid="agent-detail-params">
      <IconSectionHeader
        icon={Settings2}
        className="mb-2 gap-1.5"
        labelAs="h3"
        labelClassName="font-semibold"
      >
        Launch Configuration
      </IconSectionHeader>
      <InfoPanel>
        <DetailList>
          {/* Shared with the persona detail page so agents and the personas
              they launch from read identically. */}
          <AgentConfigDetailFields
            config={{
              agentType: agent.agentType,
              model: agent.model,
              modelProviderSecretId: agent.modelProviderSecretId,
              attachments: agent.attachments,
              blueprintId: agent.blueprintId,
              blueprintName: agent.blueprintName,
              sandboxOptions: agent.sandboxOptions,
            }}
          />
          {agent.personaId ? (
            <DetailField icon={UserCircle} label="Persona">
              {/* Same hover-preview chip as blueprints/provider keys; the
                  popover resolves the full live persona. */}
              <ResourceChip kind="personas" id={agent.personaId} name={persona?.name} />
            </DetailField>
          ) : null}
          {/* The system prompt's full text lives in its own panel section
              (AgentDetailPanel), so no summary row here. */}
          {envVarCount > 0 ? (
            <DetailField icon={ListChecks} label="Env vars" value={`${envVarCount} configured`} />
          ) : null}
        </DetailList>
        <AgentSubscriptionSelector agent={agent} />
        {!hasAnyDetail && (
          <p className="text-xs text-muted-foreground italic pl-7 pt-1">
            No additional launch configuration
          </p>
        )}
      </InfoPanel>
    </section>
  );
}
